import React, { useState } from "react"
import classNames from "classnames"
import { AnimatePresence, motion } from "motion/react"
import Scroll from "@hg/scroll"

import { useTranslation } from "@/locales"
import { getRoguelikeItem } from "@/utils/assets"
import BookContainer from "../../components/BookContainer"
import Exit from "../../components/Exit"
import Nav from "../../components/Nav"
import TabButton from "../../components/TabButton"
import { ESubPage, ISubPageProps } from "../../context"
import { useTopicI18nStore } from "../../locales"
import SpecialRelicShelf from "./SpecialRelicShelf"

import styles from "./index.module.less"

const DESC_ANIMATION = {
    initial: {
        opacity: 0,
    },
    animate: {
        opacity: 1,
        transition: {
            duration: 0.5,
            ease: "easeInOut" as const,
        },
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 0.5,
            ease: "easeIn" as const,
        },
    },
}

const NormalRelic: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const {
        objects: { relicGroups: RELIC_DATA, relicDict: RELIC_DICT },
    } = useTopicI18nStore()
    const [currentRelic, setCurrentRelic] = useState<string>(
        RELIC_DATA[0].items[0].id
    )
    return (
        <BookContainer
            className={classNames(styles.normalRelic, className)}
            style={style}
            type="relic"
        >
            <AnimatePresence mode="wait">
                <React.Fragment key={`relic-${currentRelic}`}>
                    <motion.div
                        key={`relic-title-${currentRelic}`}
                        {...DESC_ANIMATION}
                        className={classNames(
                            styles.relicTitle,
                            RELIC_DICT[currentRelic].name.length > 25
                                ? styles.small
                                : ""
                        )}
                    >
                        {RELIC_DICT[currentRelic].name
                            .split("\n")
                            .map((line, index, array) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index !== array.length - 1 && <br />}
                                </React.Fragment>
                            ))}
                    </motion.div>
                    <motion.div
                        key={`relic-image-${currentRelic}`}
                        {...DESC_ANIMATION}
                        className={styles.relicImage}
                        style={{
                            backgroundImage: `url(${getRoguelikeItem(currentRelic)})`,
                        }}
                    ></motion.div>
                    <motion.div
                        key={`relic-effect-${currentRelic}`}
                        {...DESC_ANIMATION}
                        className={classNames(
                            styles.relicEffect,
                            RELIC_DICT[currentRelic].usage.length > 160 &&
                                styles.small
                        )}
                    >
                        {RELIC_DICT[currentRelic].usage}
                    </motion.div>
                    <motion.div
                        key={`relic-desc-${currentRelic}`}
                        {...DESC_ANIMATION}
                        className={styles.relicDesc}
                    >
                        <Scroll
                            direction="y"
                            className={styles.scroll}
                            rotateDetection
                            scrollBar={true}
                        >
                            {RELIC_DICT[currentRelic].desc}
                        </Scroll>
                    </motion.div>
                </React.Fragment>
            </AnimatePresence>

            <Scroll
                className={styles.scrollContainer}
                direction="y"
                rotateDetection
            >
                <div className={styles.relicList}>
                    {RELIC_DATA.map((relicGroup, index) => (
                        <div className={styles.relicGroup} key={index}>
                            <div
                                className={classNames(
                                    styles.title,
                                    relicGroup.name.length > 18
                                        ? styles.small
                                        : ""
                                )}
                            >
                                <span>{relicGroup.name}</span>
                            </div>
                            <div className={styles.list}>
                                {relicGroup.items.map((relic, index) => (
                                    <div
                                        className={classNames(
                                            styles.relicItem,
                                            currentRelic === relic.id &&
                                                styles.active
                                        )}
                                        key={index}
                                        onClick={() =>
                                            setCurrentRelic(relic.id)
                                        }
                                    >
                                        <img
                                            className={styles.relicItemImage}
                                            src={getRoguelikeItem(relic.id)}
                                            loading="lazy"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Scroll>
        </BookContainer>
    )
}

const PageRelic: React.FC<ISubPageProps> = (props) => {
    const { className, style, onExit } = props
    const { t } = useTranslation()
    const [showButton, setShowButton] = useState(true)

    const [currentCate, setCurrentCate] = useState<"normal" | "special">(
        "special"
    )
    return (
        <motion.div
            className={classNames(styles.pageContainer, className)}
            style={style}
            initial={{
                opacity: 0,
                pointerEvents: "none",
            }}
            animate={{
                opacity: 1,
                pointerEvents: "auto",
            }}
            exit={{
                opacity: 0,
                pointerEvents: "none",
            }}
            transition={{
                duration: 0.5,
                ease: "easeInOut",
            }}
        >
            <AnimatePresence mode="wait">
                {currentCate === "normal" && <NormalRelic key={currentCate} />}
                {currentCate === "special" && (
                    <SpecialRelicShelf
                        key={currentCate}
                        setShowButton={setShowButton}
                    />
                )}
            </AnimatePresence>
            {/* <button
                style={{ position: "absolute", top: 0, left: 0 }}
                onClick={() =>
                    setCurrentCate(
                        currentCate === "normal" ? "special" : "normal"
                    )
                }
            >
                {currentCate}
            </button> */}
            <div
                className={classNames(
                    styles.buttonList,
                    (currentCate === "normal" || showButton) && styles.show
                )}
            >
                <TabButton
                    label={t("relic.tab.2")}
                    active={currentCate === "special"}
                    onClick={() => setCurrentCate("special")}
                />
                <TabButton
                    label={t("relic.tab.1")}
                    active={currentCate === "normal"}
                    onClick={() => setCurrentCate("normal")}
                />
            </div>

            <Nav page={ESubPage.Relic} />
            <Exit onClick={onExit} />
        </motion.div>
    )
}

export default PageRelic
